import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { getMovieBackdrops } from '../lib/tmdb'
import { track, Events } from '../lib/analytics'
import Logo from '../components/Logo'

export default function SharedResult() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [state, setState] = useState({ phase: 'loading', data: null })
  const [backdrops, setBackdrops] = useState([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      const { data, error } = await supabase.rpc('get_shared_result', { p_slug: slug })
      if (cancelled) return
      if (error || !data) {
        setState({ phase: 'not_found', data: null })
        return
      }
      setState({ phase: 'ready', data })
      if (data.movie?.tmdb_id) {
        getMovieBackdrops(data.movie.tmdb_id).then(imgs => {
          if (!cancelled) setBackdrops(imgs)
        }).catch(() => { /* ignore */ })
      }
    }
    load()
    return () => { cancelled = true }
  }, [slug])

  // Fire "Shared Link Viewed" once per mount, once auth is settled so we can
  // report is_authed accurately.
  useEffect(() => {
    if (authLoading) return
    if (state.phase !== 'ready') return
    track(Events.SharedLinkViewed, {
      mode: state.data?.mode || 'unknown',
      is_authed: user ? 'true' : 'false',
    })
  }, [authLoading, state.phase, user, state.data?.mode])

  if (state.phase === 'loading') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center" style={{ background: 'var(--fp-bg)' }}>
        <span className="text-[var(--fp-text-muted)] text-sm">Loading…</span>
      </div>
    )
  }

  if (state.phase === 'not_found') {
    return <NotFound isAuthed={!!user} onStart={() => navigate(user ? '/' : '/login')} />
  }

  const { data } = state
  const movie = data.movie || {}
  const picker = data.picker_first_name
  const suggester = data.suggester_first_name
  const isSolo = data.mode === 'solo'
  const tagline = isSolo
    ? (picker ? `FlickPick chose this for ${picker}` : 'FlickPick chose this')
    : (picker ? `FlickPick chose this for ${picker}'s crew` : 'FlickPick chose this')

  return (
    <div className="min-h-[100dvh] relative overflow-hidden flex flex-col" style={{ background: 'var(--fp-bg)', color: 'var(--fp-text)' }}>
      {/* Ambient backdrop */}
      {backdrops[0] && (
        <div className="fixed inset-0 z-0">
          <img
            src={backdrops[0]}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.3) saturate(1.15)' }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'radial-gradient(ellipse at center, transparent 10%, rgba(0,0,0,0.85) 90%)' }}
          />
        </div>
      )}

      <div className="relative z-10 flex-1 flex flex-col items-center px-5 pt-10 pb-16">
        <Link to={user ? '/' : '/login'} className="no-underline">
          <Logo size="sm" />
        </Link>

        {/* Hero — above the fold on mobile */}
        <div className="flex-1 flex flex-col items-center justify-center w-full max-w-2xl mt-6">
          <div
            className="text-[12px] fp-gold-text uppercase tracking-[0.3em] font-semibold mb-3 text-center"
            style={{ textShadow: '0 0 24px rgba(212, 175, 55, 0.5)' }}
          >
            {tagline}
          </div>

          <div
            className="rounded-2xl overflow-hidden mx-auto bg-[var(--fp-border)]"
            style={{
              width: 'min(240px, 60vw)',
              aspectRatio: '2/3',
              boxShadow: '0 0 80px var(--fp-red-glow), 0 0 140px rgba(212, 175, 55, 0.2), 0 12px 40px rgba(0,0,0,0.7)',
              border: '3px solid var(--fp-gold)',
            }}
          >
            {movie.poster_url ? (
              <img
                src={movie.poster_url}
                alt={movie.title}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-6xl">🎬</div>
            )}
          </div>

          <h1
            className="fp-display text-4xl sm:text-5xl text-white mt-8 text-center"
            style={{ textShadow: '0 2px 24px rgba(0,0,0,0.7), 0 0 40px var(--fp-red-glow)', letterSpacing: '-0.03em' }}
          >
            {movie.title || 'Untitled'}
          </h1>

          <p className="text-sm text-[var(--fp-text-muted)] mt-2 text-center">
            {[
              movie.year,
              movie.genre,
              movie.runtime_minutes && `${Math.floor(movie.runtime_minutes / 60)}h ${movie.runtime_minutes % 60}m`,
              movie.tmdb_rating && `★ ${movie.tmdb_rating}`,
            ].filter(Boolean).join(' · ')}
          </p>

          {!isSolo && suggester && (
            <p className="text-[13px] mt-2" style={{ color: 'var(--fp-red)' }}>
              Picked by {suggester}
            </p>
          )}

          <button
            onClick={() => navigate(user ? '/' : '/login')}
            className="fp-btn fp-btn-primary mt-10 max-w-sm"
            style={{
              padding: '18px 32px',
              fontSize: '16px',
              boxShadow: '0 8px 32px var(--fp-red-glow)',
              minHeight: '44px',
            }}
          >
            Start your own movie night
          </button>

          <a
            href="#what-is-flickpick"
            className="mt-4 text-sm text-[var(--fp-text-muted)] hover:text-white transition-colors"
          >
            What is FlickPick? ↓
          </a>
        </div>

        {/* Explainer — below the fold */}
        <section
          id="what-is-flickpick"
          className="w-full max-w-xl mt-32 scroll-mt-20"
        >
          <h2 className="fp-display text-2xl text-white mb-5 text-center">
            What is FlickPick?
          </h2>
          <p className="text-sm text-[var(--fp-text-muted)] leading-relaxed mb-4">
            The "what should we watch?" debate ends here. Everyone in your crew picks one movie.
            The wheel spins. Fate decides. No arguments, no rewatching the same three shows.
          </p>
          <p className="text-sm text-[var(--fp-text-muted)] leading-relaxed mb-4">
            Solo? Load up your watchlist and let the wheel break your decision paralysis.
          </p>
          <p className="text-sm text-[var(--fp-text-muted)] leading-relaxed mb-8">
            Zero tracking. No ads. Free while it's small.
          </p>

          <button
            onClick={() => navigate(user ? '/' : '/login')}
            className="fp-btn fp-btn-primary"
            style={{ minHeight: '44px' }}
          >
            Start your own movie night
          </button>

          <p className="text-center text-[11px] text-[var(--fp-text-dim)] mt-8">
            Someone has to decide.
          </p>
        </section>
      </div>
    </div>
  )
}

function NotFound({ isAuthed, onStart }) {
  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 text-center" style={{ background: 'var(--fp-bg)' }}>
      <Logo size="md" />
      <div className="text-5xl mt-8 mb-4">🎬</div>
      <h1 className="fp-display text-3xl text-white">This reel's empty</h1>
      <p className="text-sm text-[var(--fp-text-muted)] mt-2 max-w-sm">
        We couldn't find a movie night at this link. It may have been removed, or the link is a typo.
      </p>
      <button
        onClick={onStart}
        className="fp-btn fp-btn-primary mt-8 max-w-xs"
        style={{ minHeight: '44px' }}
      >
        Start your own movie night
      </button>
      <p className="text-center text-[11px] text-[var(--fp-text-dim)] mt-8">
        Someone has to decide.
      </p>
    </div>
  )
}
